import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TraderDetail from '../components/TraderDetail';
import { traderService, type Trader, type TraderFeatures } from '../services/traderService';
import { Skeleton } from '../components/ui/skeleton';

const TraderDetailPage: React.FC = () => {
  const { slug: slugOrId } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [trader, setTrader] = useState<(Trader & { trader_features?: TraderFeatures }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrader = async () => {
      if (!slugOrId) {
        setError('No trader identifier provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        let traderData = await traderService.getTraderBySlug(slugOrId);
        let fetchedById = false;

        if (!traderData) {
          traderData = await traderService.getTraderById(slugOrId);
          fetchedById = Boolean(traderData);
        }

        if (!traderData) {
          throw new Error('Trader not found');
        }

        if (fetchedById && traderData.slug && traderData.slug !== slugOrId) {
          navigate(`/trader/${traderData.slug}`, { replace: true });
        }

        setTrader(traderData);
      } catch (err) {
        console.error('Error fetching trader:', err);
        setError('Failed to load trader details');
      } finally {
        setLoading(false);
      }
    };

    fetchTrader();
  }, [slugOrId, navigate]);

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6 mt-16">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-6 w-32" />
                <div className="space-y-3">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-5 w-56" />
                  <Skeleton className="h-5 w-40" />
                </div>
              </div>
              <div className="flex justify-center">
                <Skeleton className="w-full max-w-sm aspect-square rounded-lg" />
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-200">
              <Skeleton className="h-6 w-24 mb-4" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !trader) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Trader Not Found</h2>
            <p className="text-gray-600 mb-6">
              {error || "The trader you're looking for doesn't exist or may have been removed."}
            </p>
            <button
              onClick={handleBack}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <TraderDetail trader={trader} onBack={handleBack} />;
};

export default TraderDetailPage;
